import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Calendar, ClipboardList } from "lucide-react";
import { type ReactNode, useState } from "react";

import PurchaseRequestApprovalPanel from "@/components/approvals/purchase-request-approval-panel";
import CommentsThread from "@/components/comments-thread";
import { HelpButton, HelpDialog } from "@/components/help-dialog";
import StatusBadge from "@/components/request-status/status-badge";
import StatusHistoryDialog from "@/components/request-status/status-history-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Item,
  ItemGroup,
} from "@/components/ui/item";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import AppLayout from "@/layouts/app-layout";
import { resolveSelectionsTotalArs } from "@/lib/purchase-selections";
import type {
  BreadcrumbItem,
  Comment,
  PurchaseRequest,
  PurchaseRequestApprovalState,
  PurchaseRequestAssignments,
} from "@/types";
import { formatDateAR } from "@/utils/date";

import type {
  PurchaseRequestDecisionItem,
  PurchaseRequestDecisionSupplierSummary,
} from "../hooks/use-purchase-request-decision-items";

import PurchaseRequestDecisionItemsCards, {
  PurchaseRequestDecisionSummaryCards,
} from "./purchase-request-decision-items-cards";

interface StatusHistoryItem {
  at: string;
  by?: { id: number | null; name?: string | null } | null;
  from?: string | null;
  to?: string | null;
}

interface PurchaseRequestShowLayoutSimpleProps {
  purchaseRequest: PurchaseRequest;
  approval: PurchaseRequestApprovalState;
  decisionItems: PurchaseRequestDecisionItem[];
  selectedSuppliers: PurchaseRequestDecisionSupplierSummary[];
  comments: Comment[];
  commentsHref: string;
  statusHistory: StatusHistoryItem[];
  purchaseRequestsHref: string;
  purchaseRequestHref: string;
  viewModeToggle?: ReactNode;
}

interface SimpleSectionProps {
  title: string;
  description: string;
  children: ReactNode;
  headerAction?: ReactNode;
}

const UNASSIGNED_ASSIGNEE_LABEL = "Sin asignar";

function formatAssigneeName(
  user: PurchaseRequestAssignments["buyer"],
): string {
  return user?.full_name ?? user?.email ?? UNASSIGNED_ASSIGNEE_LABEL;
}

function SimpleSection({
  title,
  description,
  children,
  headerAction,
}: SimpleSectionProps) {
  return (
    <Card className="border-border/80">
      <CardHeader className="border-b bg-muted/20">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-primary">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {headerAction}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export default function PurchaseRequestShowLayoutSimple({
  purchaseRequest,
  approval,
  decisionItems,
  selectedSuppliers,
  comments,
  commentsHref,
  statusHistory,
  purchaseRequestsHref,
  purchaseRequestHref,
  viewModeToggle,
}: PurchaseRequestShowLayoutSimpleProps) {
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const selectedProductsHelp = useHelpDialog("selectedProductsSimpleView");

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Solicitudes Compras", href: purchaseRequestsHref },
    { title: `Solicitud #${purchaseRequest.id}`, href: "#" },
  ];

  const selectedTotal = resolveSelectionsTotalArs(approval);
  const buyerName = formatAssigneeName(purchaseRequest.assignments?.buyer);
  const requesterName = formatAssigneeName(purchaseRequest.assignments?.requester);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Solicitud #${purchaseRequest.id} · Vista simple`} />

      <div className="flex h-full flex-1 flex-col gap-4 p-4 sm:gap-6 sm:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2">
              <Link href={purchaseRequestsHref}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver a solicitudes
              </Link>
            </Button>
            <h1 className="text-2xl font-bold sm:text-3xl">
              Solicitud #{purchaseRequest.id}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <StatusBadge status={purchaseRequest.status} />
              <span>·</span>
              <button
                type="button"
                onClick={() => setHistoryDialogOpen(true)}
                className="underline"
              >
                Ver historial de estado
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            {viewModeToggle}
            <Button size="sm" variant="outline" asChild>
              <Link href={`${purchaseRequestHref}/activity_log`}>
                <ClipboardList className="h-4 w-4 mr-1" /> Ver actividad
              </Link>
            </Button>
          </div>
        </div>

        <SimpleSection
          title="Información general"
          description="Contexto de la solicitud para tomar la decisión."
        >
          <ItemGroup className="grid gap-2 md:grid-cols-2">
            <Item
              variant="outline"
              size="sm"
              className="border-border/80"
            >
              <div className="w-full space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">Sector</p>
                <p className="text-sm font-medium">
                  {purchaseRequest.sector?.name ?? "Sin sector"}
                  {purchaseRequest.sector?.code
                    ? ` · Nº ${purchaseRequest.sector.code}`
                    : ""}
                </p>
              </div>
            </Item>
            <Item
              variant="outline"
              size="sm"
              className="border-border/80"
            >
              <div className="w-full space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">
                  Fecha de solicitud
                </p>
                <p className="flex items-center gap-1 text-sm font-medium">
                  <Calendar className="h-3.5 w-3.5" />
                  {purchaseRequest.request_date
                    ? formatDateAR(purchaseRequest.request_date)
                    : "Sin fecha"}
                </p>
              </div>
            </Item>
            <Item variant="outline" size="sm" className="border-border/80">
              <div className="w-full space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">
                  Comprador
                </p>
                <p className="text-sm font-medium">{buyerName}</p>
              </div>
            </Item>
            <Item variant="outline" size="sm" className="border-border/80">
              <div className="w-full space-y-0.5">
                <p className="text-xs font-medium text-muted-foreground">
                  Solicitante
                </p>
                <p className="text-sm font-medium">{requesterName}</p>
              </div>
            </Item>
          </ItemGroup>
        </SimpleSection>

        <SimpleSection
          title="Productos seleccionados"
          description="Listado de ítems elegidos y alternativas por producto."
          headerAction={
            <HelpButton
              onClick={selectedProductsHelp.open}
              label="Ayuda"
              variant="outline"
            />
          }
        >
          <PurchaseRequestDecisionItemsCards items={decisionItems} />
        </SimpleSection>

        <SimpleSection
          title="Condiciones comerciales y total"
          description="Resumen por proveedor seleccionado y total consolidado."
        >
          <PurchaseRequestDecisionSummaryCards
            selectedSuppliers={selectedSuppliers}
            totalArs={selectedTotal}
          />
        </SimpleSection>

        <SimpleSection
          title="Comentarios"
          description="Razones y contexto adicional para la decisión."
        >
          <Accordion
            type="single"
            collapsible
            className="w-full rounded-md border px-3"
          >
            <AccordionItem value="comments">
              <AccordionTrigger>
                Ver comentarios ({comments.length})
              </AccordionTrigger>
              <AccordionContent>
                <CommentsThread
                  comments={comments}
                  commentsPath={commentsHref}
                  resourceLabel="la solicitud"
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </SimpleSection>

        <section className="space-y-2 rounded-md border p-2">
          <div className="flex items-center gap-2 px-1">
            <h2 className="text-base font-semibold text-primary">Aprobación</h2>
          </div>
          <PurchaseRequestApprovalPanel
            approval={approval}
            purchaseRequestId={purchaseRequest.id}
            sectorId={purchaseRequest.sector?.id}
            sectorName={purchaseRequest.sector?.name}
          />
        </section>

        <StatusHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          currentStatus={purchaseRequest.status}
          history={statusHistory}
        />

        <HelpDialog
          content={selectedProductsHelp.content}
          open={selectedProductsHelp.isOpen}
          onOpenChange={selectedProductsHelp.setIsOpen}
        />
      </div>
    </AppLayout>
  );
}
